import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Search, CalendarDays, ShoppingBasket, Activity, ThumbsUp } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { PhoneFrame, ScreenMatch, ScreenPlanner, ScreenShopping } from './PhoneMockup';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Features = () => {
  const { t } = useLanguage();
  const f = t.features.list;

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-24 md:py-32 bg-[#FFFDF8]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* heading */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <motion.span
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={fadeUp}
            className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold mb-5"
          >
            {t.features.eyebrow}
          </motion.span>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            custom={1}
            className="font-display font-extrabold tracking-tight text-stone-900 text-4xl md:text-5xl lg:text-6xl text-balance"
          >
            {t.features.title}
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            custom={2}
            className="mt-4 text-base md:text-lg text-stone-600 max-w-2xl"
          >
            {t.features.subtitle}
          </motion.p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {/* Card 1 — Meal Match (large left) */}
          <motion.article
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            data-testid="feature-match"
            className="group md:col-span-7 relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 p-8 md:p-10 text-white min-h-[420px]"
          >
            <div className="absolute -right-12 -bottom-12 h-72 w-72 rounded-full bg-orange-400/40 blur-2xl pointer-events-none" />
            <div className="relative z-10 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-5">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-[10px] font-bold uppercase tracking-wider">
                  <Sparkles className="h-3 w-3" /> {f.match.tag}
                </span>
              </div>
              <h3 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">
                {f.match.title}
              </h3>
              <p className="mt-3 text-white/90 max-w-md text-base">{f.match.desc}</p>
              <div className="mt-auto pt-8 flex items-end justify-end -mb-4 -mr-4 md:-mb-6 md:-mr-6">
                <div className="scale-[0.55] sm:scale-[0.65] origin-bottom-right">
                  <PhoneFrame tilt={6}>
                    <ScreenMatch />
                  </PhoneFrame>
                </div>
              </div>
            </div>
          </motion.article>

          {/* Card 2 — Discover (small top right) */}
          <motion.article
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={1}
            data-testid="feature-discover"
            className="md:col-span-5 relative overflow-hidden rounded-3xl bg-white border border-stone-100 p-8 min-h-[420px] hover:shadow-[0_20px_40px_rgba(249,115,22,0.1)] transition-shadow"
          >
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-stone-100 text-stone-700 text-[10px] font-bold uppercase tracking-wider mb-5">
              <Search className="h-3 w-3" /> {f.discover.tag}
            </span>
            <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-stone-900">
              {f.discover.title}
            </h3>
            <p className="mt-3 text-stone-600">{f.discover.desc}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {['Fisk', 'Vegetar', '< 30 min', 'Budsjett', 'Pasta', 'Kjøtt'].map((c, i) => (
                <span
                  key={i}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                    i === 0 || i === 2
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-stone-700 border-stone-200'
                  }`}
                >
                  {c}
                </span>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2">
              {[
                'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=300&q=70',
                'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=300&q=70',
                'https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=300&q=70',
              ].map((src, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-2xl overflow-hidden shadow-sm transition-transform hover:-translate-y-1"
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </motion.article>

          {/* Card 3 — Planner (medium left) */}
          <motion.article
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            data-testid="feature-planner"
            className="md:col-span-5 relative overflow-hidden rounded-3xl bg-stone-900 text-white p-8 min-h-[400px]"
          >
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-wider mb-5">
              <CalendarDays className="h-3 w-3" /> {f.planner.tag}
            </span>
            <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">
              {f.planner.title}
            </h3>
            <p className="mt-3 text-stone-300 max-w-sm">{f.planner.desc}</p>
            <div className="mt-6 flex justify-center -mb-6">
              <div className="scale-[0.7] origin-top">
                <PhoneFrame tilt={-3}>
                  <ScreenPlanner />
                </PhoneFrame>
              </div>
            </div>
          </motion.article>

          {/* Card 4 — Shopping (medium right) */}
          <motion.article
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={1}
            data-testid="feature-list"
            className="md:col-span-7 relative overflow-hidden rounded-3xl bg-[#FFF1E7] p-8 md:p-10 min-h-[400px]"
          >
            <div className="flex flex-col md:flex-row gap-6 h-full">
              <div className="flex-1">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white text-orange-700 text-[10px] font-bold uppercase tracking-wider mb-5">
                  <ShoppingBasket className="h-3 w-3" /> {f.list.tag}
                </span>
                <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-stone-900">
                  {f.list.title}
                </h3>
                <p className="mt-3 text-stone-700 max-w-md">{f.list.desc}</p>

                <div className="mt-6 space-y-2">
                  {[
                    { name: 'Kyllingfilet · 500 g', done: true },
                    { name: 'Sitron · 2 stk', done: true },
                    { name: 'Quinoa · 200 g', done: false },
                    { name: 'Fersk persille · 1 bunt', done: false },
                  ].map((it, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white shadow-sm"
                    >
                      <span
                        className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                          it.done
                            ? 'bg-orange-500 border-orange-500 text-white text-xs'
                            : 'border-stone-300'
                        }`}
                      >
                        {it.done && '✓'}
                      </span>
                      <span
                        className={`text-sm font-semibold ${
                          it.done ? 'line-through text-stone-400' : 'text-stone-900'
                        }`}
                      >
                        {it.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="hidden md:flex items-center justify-center flex-shrink-0">
                <div className="scale-[0.7] origin-center">
                  <PhoneFrame tilt={4}>
                    <ScreenShopping />
                  </PhoneFrame>
                </div>
              </div>
            </div>
          </motion.article>

          {/* Card 5 — Nutrition */}
          <motion.article
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            data-testid="feature-nutrition"
            className="md:col-span-6 relative overflow-hidden rounded-3xl bg-white border border-stone-100 p-8 min-h-[280px]"
          >
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider mb-5">
              <Activity className="h-3 w-3" /> {f.nutrition.tag}
            </span>
            <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-stone-900">
              {f.nutrition.title}
            </h3>
            <p className="mt-3 text-stone-600 max-w-md">{f.nutrition.desc}</p>
            <div className="mt-6 grid grid-cols-4 gap-3">
              {[
                { label: 'Kcal', v: '420', c: 'bg-orange-50 text-orange-700' },
                { label: 'Protein', v: '38g', c: 'bg-rose-50 text-rose-700' },
                { label: 'Fett', v: '22g', c: 'bg-amber-50 text-amber-700' },
                { label: 'Karbo', v: '14g', c: 'bg-lime-50 text-lime-700' },
              ].map((n, i) => (
                <div key={i} className={`rounded-2xl p-3 ${n.c}`}>
                  <div className="text-[10px] font-bold uppercase opacity-70">{n.label}</div>
                  <div className="text-xl font-extrabold mt-0.5">{n.v}</div>
                </div>
              ))}
            </div>
          </motion.article>

          {/* Card 6 — Favorites */}
          <motion.article
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={1}
            data-testid="feature-favorites"
            className="md:col-span-6 relative overflow-hidden rounded-3xl bg-stone-100 p-8 min-h-[280px]"
          >
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white text-stone-700 text-[10px] font-bold uppercase tracking-wider mb-5">
              <ThumbsUp className="h-3 w-3" /> {f.favorites.tag}
            </span>
            <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-stone-900">
              {f.favorites.title}
            </h3>
            <p className="mt-3 text-stone-700 max-w-md">{f.favorites.desc}</p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              {[
                { emoji: '❤️', label: 'Likt' },
                { emoji: '🍝', label: 'Pasta' },
                { emoji: '🥗', label: 'Sunt' },
                { emoji: '🚫', label: 'Sopp' },
                { emoji: '⚡', label: 'Rask' },
              ].map((p, i) => (
                <span
                  key={i}
                  className="px-3 py-2 rounded-full bg-white shadow-sm text-sm font-semibold text-stone-800 flex items-center gap-1.5"
                >
                  <span>{p.emoji}</span> {p.label}
                </span>
              ))}
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
};
